import { useEffect, useState } from "react"

import type {
  DateScope,
  FindVacationsData,
  StayScope,
  VacationSearchCriteria,
  VacationSearchPagy,
  VacationSearchResponse,
  VacationSearchResult,
} from "./findVacationsData"
import { VacationSearchControls } from "./VacationSearchControls"
import { VacationSearchResults } from "./VacationSearchResults"

type FindVacationsProps = {
  data: FindVacationsData
}

export function FindVacations({ data }: FindVacationsProps) {
  const [dateScope, setDateScope] = useState<DateScope>(data.initial_date_scope)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [lastSearch, setLastSearch] = useState<VacationSearchCriteria | null>(null)
  const [pagy, setPagy] = useState<VacationSearchPagy | null>(null)
  const [query, setQuery] = useState(data.initial_query)
  const [results, setResults] = useState<readonly VacationSearchResult[]>([])
  const [stayScope, setStayScope] = useState<StayScope>(data.initial_stay_scope)

  useEffect(() => {
    if (data.search_on_load) {
      void runFreshSearch({
        dateScope: data.initial_date_scope,
        query: data.initial_query,
        stayScope: data.initial_stay_scope,
      })
    }
  }, [])

  async function handleSubmit(search: VacationSearchCriteria) {
    await runFreshSearch(search)
  }

  async function handleDateScopeChange(nextDateScope: DateScope) {
    if (isLoading || nextDateScope === dateScope) return

    await runFreshSearch({ dateScope: nextDateScope, query: query.trim(), stayScope })
  }

  async function handleStayScopeChange(nextStayScope: StayScope) {
    if (isLoading || nextStayScope === stayScope) return

    await runFreshSearch({ dateScope, query: query.trim(), stayScope: nextStayScope })
  }

  async function handleShowMore() {
    if (!lastSearch || !pagy?.next) return

    await runSearch(lastSearch, pagy.next, true)
  }

  async function runFreshSearch(search: VacationSearchCriteria) {
    const normalizedSearch = { ...search, query: search.query.trim() }

    setDateScope(normalizedSearch.dateScope)
    setLastSearch(normalizedSearch)
    setQuery(normalizedSearch.query)
    setStayScope(normalizedSearch.stayScope)
    updateSearchUrl(normalizedSearch, data)
    await runSearch(normalizedSearch, 1, false)
  }

  async function runSearch(search: VacationSearchCriteria, page: number, append: boolean) {
    setError(null)
    setHasSearched(true)
    setIsLoading(true)
    if (!append) {
      setPagy(null)
      setResults([])
    }

    try {
      const payload = await fetchVacations(search, page, data)
      setPagy(payload.pagy)
      setResults((currentResults) => (append ? [...currentResults, ...payload.data] : payload.data))
    } catch {
      setError("Vacation search could not be loaded.")
    } finally {
      setIsLoading(false)
    }
  }

  const hasMoreResults = Boolean(pagy?.next)

  return (
    <section className="grid gap-3">
      <header>
        <h1 className="m-0 text-[24px] font-bold leading-8 text-[#424b58]">Find Vacations</h1>
      </header>

      <VacationSearchControls
        dateScope={dateScope}
        disabled={isLoading}
        onDateScopeChange={handleDateScopeChange}
        onQueryChange={setQuery}
        onStayScopeChange={handleStayScopeChange}
        onSubmit={handleSubmit}
        query={query}
        stayScope={stayScope}
      />

      <VacationSearchResults
        error={error}
        hasMoreResults={hasMoreResults}
        hasSearched={hasSearched}
        isLoading={isLoading}
        onShowMore={handleShowMore}
        results={results}
      />
    </section>
  )
}

async function fetchVacations(search: VacationSearchCriteria, page: number, data: FindVacationsData) {
  const params = new URLSearchParams({
    date_scope: search.dateScope,
    page: String(page),
    query: search.query,
    stay_scope: search.stayScope,
  })

  data.selected_camp_ids.forEach((campId) => params.append("selected_camp_ids[]", campId))

  const response = await fetch(`${data.search_endpoint}?${params.toString()}`, {
    headers: { Accept: "application/json" },
  })

  if (!response.ok) {
    throw new Error(`Vacation search failed with ${response.status}`)
  }

  return (await response.json()) as VacationSearchResponse
}

function updateSearchUrl(search: VacationSearchCriteria, data: FindVacationsData) {
  const params = new URLSearchParams()
  const selectedCampId = data.selected_camp_ids[0]

  if (selectedCampId) {
    params.set("camp_id", selectedCampId)
  }

  params.set("date_scope", search.dateScope)
  params.set("stay_scope", search.stayScope)

  if (search.query.length > 0) {
    params.set("query", search.query)
  }

  const nextPath = `${window.location.pathname}?${params.toString()}${window.location.hash}`
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextPath === currentPath) return

  window.history.replaceState(null, "", nextPath)
}
